import { action } from "mobx";
import {
  TreeSelect,
  TreeSelectChangeParams,
  TreeSelectProps,
  TreeSelectSelectionModeType,
} from "primereact/treeselect";
import React, { MutableRefObject, useEffect, useRef } from "react";
import { separator } from "../WSUtils/keySeparator";
import {
  concatClassnames,
  findSelfOrParent,
  persistAndStopPropagation,
} from "../WSUtils/util";
import { getPanelScrollHeight } from "./getPanelScrollHeight";
import { useCustomAlignTreeSelectOverlay } from "./useCustomAlignOverlay";

export type TreeSelectOnChangeResult = [{ [key: string]: true }, boolean];

/**
 * Enable expanding of group when clicking on a group header/label.
 * Out of the box you can only expand group by
 * clicking on the chevron
 */
export const treeSelectOnChange = (
  evt: TreeSelectChangeParams
): TreeSelectOnChangeResult => {
  const entries =
    typeof evt.value === "string"
      ? ([[evt.value, true]] as [string, true][])
      : Object.entries(evt.value);

  const clickedOnGroupLabel = !!entries.find(([key, val]) =>
    key.startsWith(`GROUP${separator}`)
  );

  // if clicked on label/header of group then expand it
  // (i found no good way to solve this, so here is a hacky solution)
  if (clickedOnGroupLabel) {
    const treeNodeContent: HTMLDivElement = findSelfOrParent(
      evt.originalEvent.target,
      (p) => p.classList.contains("p-treenode-content")
    );

    const expandButton: HTMLButtonElement =
      !!treeNodeContent && treeNodeContent.querySelector(".p-tree-toggler");

    if (expandButton) {
      expandButton.click();
      expandButton.focus();
    }
  }

  return [
    entries
      .filter(([key, val]) => !key.startsWith(`GROUP${separator}`) && val)
      .reduce((acc, [key, val]) => {
        acc[key] = true;
        return acc;
      }, {}),
    clickedOnGroupLabel,
  ];
};

export const stopTreeSelectScroll = (ref: TreeSelect) => {
  // prevent scrolling select panel (it is bugged)
  if (ref) (ref as any).scrollInView = () => {};
};

export const useStopTreeSelectScroll = (ref?: MutableRefObject<any>) => {
  const _ref = useRef(null);
  ref = ref || _ref;

  useEffect(() => {
    stopTreeSelectScroll(ref?.current);
  });

  return ref;
};

export const treeSelectCustomValueTemplate: TreeSelectProps["valueTemplate"] = (
  v,
  p
) => {
  let values = v;
  if (!Array.isArray(values)) {
    values = [values];
  }

  if (values.length === 0) {
    const message = p.emptyMessage || p.placeholder;
    return (
      <div
        className={concatClassnames([
          "cv-treeselect-empty",
          !message && "cv-treeselect-empty-no-message",
        ])}
      >
        {message || "empty"}
      </div>
    );
  }

  return values.map((v) => (
    <div key={v.key} className="p-treeselect-token">
      <span className="p-treeselect-token-label">{v.label}</span>
      <span
        className="cve-treeselect-value-remove-button pi pi-times-circle"
        onClick={action((evt) => {
          persistAndStopPropagation(evt);

          const newValue = {};
          Object.entries(p.value).forEach(([key, val]) => {
            if (v.key + "" !== key + "") newValue[key] = val;
          });

          p.onChange({
            originalEvent: evt,
            target: null,
            value: newValue,
            stopPropagation: evt.stopPropagation,
            preventDefault: evt.preventDefault,
          });
        })}
      />
    </div>
  ));
};

export const useDefaultTreeSelectProps = () => {
  const ref = useCustomAlignTreeSelectOverlay();
  useStopTreeSelectScroll(ref);

  return {
    filter: true,
    scrollHeight: getPanelScrollHeight(),
    metaKeySelection: false,
    selectionMode: "multiple" as TreeSelectSelectionModeType,
    ref,
  };
};
